// IVR app publish — Phase 3 (versioned drafts, safe publish, rollback) and the Phase 4
// attach pre-check of docs/design/2026-10-14_ivr_app_management.md.
// Backend: IvrPublishController (/ivr/publish).
//
// Who may use it: Green Giraffe on any flow; a business / operator / provider admin on
// a flow filed to their organisation whose every number is held by it (the backend
// re-checks this live before a publish). The recording folder (business_name) is
// locked for them. Rollback = republish an earlier version (previewed). Removed states
// are deleted from the switch 10 minutes after a publish.
import axiosInstance from '../axios.config';
import { apiError } from '../apiError';
import type { ApiResponse } from 'types';

/** One switch state_details row, exactly as the switch stores it. */
export interface StateRow {
  operator_id?: string;
  service_id?: string;
  name: string;
  language?: string;
  call_request?: string | null;
  type?: string | null;
  action?: string | null;
  context_type?: string | null;
  /** JSON text. */
  action_details?: string | null;
  /** JSON text. */
  type_meta?: string | null;
  is_blocked?: number | null;
  /** The recording folder. Read-only. */
  business_name?: string | null;
  attributes?: string | null;
}

export interface Issue {
  severity: 'ERROR' | 'WARNING';
  code: string;
  state: string;
  message: string;
  preExisting?: boolean;
}

export interface DraftSummary {
  id: number;
  operatorId: string;
  serviceId: string;
  language: string;
  status: 'OPEN' | 'PUBLISHED' | 'DISCARDED';
  revision: number;
  baseProviderId?: number | null;
  baseVersionId?: number | null;
  note?: string | null;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string | null;
  updatedAt?: string | null;
  publishedBy?: string | null;
  publishedAt?: string | null;
  publishPushIds?: string | null;
  existing?: boolean;
}

export interface Draft extends DraftSummary { states: StateRow[] }

export interface VersionSummary {
  versionId: number;
  providerId: number;
  provider?: string;
  language: string;
  versionNo: number;
  source: string;
  stateCount?: number;
  businessNames?: string | null;
  duplicateKeys?: string | null;
  createdAt?: string;
  createdBy?: string;
}

export interface PublishAudit {
  id: number;
  kind: 'PUSH' | 'ROLLBACK' | 'PUBLISH' | 'REPUBLISH';
  providerId: number;
  provider?: string;
  language: string;
  result: 'APPLIED' | 'PARTIAL' | 'FAILED';
  rowsWritten?: number;
  rowsDeleted?: number;
  versionId?: number | null;
  businessNameBefore?: string | null;
  businessNameAfter?: string | null;
  pushedBy?: string;
  pushedAt?: string;
}

export interface PendingDeletion {
  id: number;
  pushId: number;
  providerId: number;
  name: string;
  language: string;
  callRequest?: string | null;
  status: 'PENDING' | 'DONE' | 'CANCELLED' | 'FAILED';
  dueAt: string;
  secondsLeft?: number;
  attempts?: number;
  note?: string | null;
  executedAt?: string | null;
  executedBy?: string | null;
}

export interface FlowDetail {
  operatorId: string;
  serviceId: string;
  master: boolean;
  manageReason?: string;
  ownership: { status: string; ownerOrgId?: string | null; ownerOrgName?: string | null };
  homeProviders: Array<{ id: number; name: string }>;
  importedFrom: Array<{ id: number; name: string }>;
  languages: string[];
  versions: VersionSummary[];
  drafts: DraftSummary[];
  publishes: PublishAudit[];
  pendingDeletions: PendingDeletion[];
  delayedDeleteJobEnabled: boolean;
  deleteGraceMinutes: number;
}

export interface TargetPreview {
  providerId: number;
  provider: string;
  callRequest: { column: string; evidence?: string };
  businessName: { onSwitch: string[]; before?: string | null; after?: string | null; change: boolean; locked: boolean };
  writes: Array<{ name: string; callRequest?: string | null; action: 'INSERT' | 'UPDATE' | 'UNCHANGED';
    diff: Record<string, { before: unknown; after: unknown }>; after?: StateRow }>;
  inserts: number;
  updates: number;
  unchanged: number;
  deletesAfterGrace: Array<{ name: string; language: string; call_request?: string | null; alreadyScheduled?: boolean }>;
  pendingDeletionsCancelled: string[];
  statesBefore: number;
  statesAfter: number;
  otherLanguagesUntouched: string[];
  drift: boolean;
  validation: Issue[];
  warnings: string[];
  blockers: string[];
}

export interface PublishResult {
  kind: 'PUBLISH' | 'REPUBLISH';
  operatorId: string;
  serviceId: string;
  language: string;
  deleteGraceMinutes: number;
  delayedDeleteJobEnabled: boolean;
  notPublishedTo?: number[];
  notPublishedToReason?: string;
  targets: TargetPreview[];
  blockers: string[];
  publishable: boolean;
  applied: boolean;
  pushIds?: number[];
  results?: Array<{ providerId: number; provider: string; pushId: number; result: string; rowsWritten: number;
    failures: string[]; deletionsScheduled: PendingDeletion[]; versionNo?: number }>;
  sourceVersionNo?: number;
  versionBusinessNames?: string[];
  note?: string;
  /** Safe publish: send this back with apply; 409 if the switch changed since. */
  previewHash?: string;
  /** off | preview | apply (cpass.ivr.switch-publish.mode). Apply is refused unless 'apply'. */
  mode?: string;
  applyEnabled?: boolean;
  /** The same preview was already applied: this is the earlier result, nothing written again. */
  replayed?: boolean;
  allApplied?: boolean;
  warning?: string;
}

export interface PublishOptions {
  apply?: boolean;
  providerIds?: number[];
  /** Master only: a new recording folder, with confirmBusinessNameChange. */
  businessName?: string;
  confirmBusinessNameChange?: boolean;
  /** Master only: publish over a hand edit on the switch. */
  acknowledgeDrift?: boolean;
  /** State names allowed to differ between languages; "*" for all. */
  languageExceptions?: string[];
  organizationId?: string;
  /** Required with apply: the previewHash of the preview the user saw. */
  previewHash?: string;
}

export interface AttachCheck {
  operatorId: string;
  serviceId: string;
  providerId: number;
  languagesOnSwitch: string[];
  presentOnSwitch: boolean;
  languageOk: boolean;
  openDrafts: string[];
  ready: boolean;
  notes: string[];
}

const BASE = '/ivr/publish';

async function call<T>(fn: () => Promise<{ data: any }>, fallback: string): Promise<ApiResponse<T>> {
  try {
    const res = await fn();
    return { success: true, data: res.data?.data as T };
  } catch (e) {
    return apiError(e, fallback);
  }
}

export const ivrPublishService = {
  flow: (operatorId: string, serviceId: string, organizationId?: string) =>
    call<FlowDetail>(() => axiosInstance.get(`${BASE}/flow`, { params: { operatorId, serviceId, organizationId } }),
      'Could not load the IVR app'),
  version: (id: number, organizationId?: string) =>
    call<VersionSummary & { states: StateRow[]; note?: string }>(
      () => axiosInstance.get(`${BASE}/versions/${id}`, { params: { organizationId } }), 'Could not load the version'),
  createDraft: (body: { operatorId: string; serviceId: string; language: string; providerId?: number;
    fromVersionId?: number; copyFromLanguage?: string; organizationId?: string }) =>
    call<Draft>(() => axiosInstance.post(`${BASE}/drafts`, body), 'Could not open a draft'),
  draft: (id: number, organizationId?: string) =>
    call<Draft>(() => axiosInstance.get(`${BASE}/drafts/${id}`, { params: { organizationId } }), 'Could not load the draft'),
  saveDraft: (id: number, revision: number, states: StateRow[], note?: string, organizationId?: string) =>
    call<Draft>(() => axiosInstance.put(`${BASE}/drafts/${id}`, { revision, states, note, organizationId }),
      'Could not save the draft'),
  discardDraft: (id: number, organizationId?: string) =>
    call<DraftSummary>(() => axiosInstance.delete(`${BASE}/drafts/${id}`, { params: { organizationId } }),
      'Could not discard the draft'),
  validate: (id: number, organizationId?: string) =>
    call<{ valid: boolean; issues: Issue[]; note?: string }>(
      () => axiosInstance.post(`${BASE}/drafts/${id}/validate`, null, { params: { organizationId } }), 'Validation failed'),
  publish: (id: number, opts: PublishOptions) =>
    call<PublishResult>(() => axiosInstance.post(`${BASE}/drafts/${id}/publish`, opts),
      opts.apply ? 'Publish failed' : 'Could not preview the publish'),
  republish: (versionId: number, opts: PublishOptions) =>
    call<PublishResult>(() => axiosInstance.post(`${BASE}/versions/${versionId}/republish`, opts),
      opts.apply ? 'Rollback failed' : 'Could not preview the rollback'),
  finalizeDeletions: (pushId?: number, operatorId?: string, serviceId?: string) =>
    call<Record<string, unknown>>(() => axiosInstance.post(`${BASE}/deletions/finalize`, null,
      { params: { pushId, operatorId, serviceId } }), 'Could not finalize the deletions'),
  attachCheck: (operatorId: string, serviceId: string, providerId: number, language?: string, organizationId?: string) =>
    call<AttachCheck>(() => axiosInstance.get(`${BASE}/attach-check`,
      { params: { operatorId, serviceId, providerId, language, organizationId } }), 'Could not check the flow'),
};

// ── pure helpers (unit-tested) ──────────────────────────────────────────────

/** A new state row the runtime can execute once its prompt is filled in. */
export function newState(name: string, language: string, callRequest: string | null): StateRow {
  return {
    name,
    language,
    call_request: callRequest,
    type: 'menu',
    action: 'static',
    context_type: 'endcall',
    action_details: JSON.stringify({ files: [''] }),
    type_meta: null,
  };
}

/** Parses a JSON text field; blank is null. */
export function parseJsonField(text: string | null | undefined):
  { ok: true; value: unknown } | { ok: false; error: string } {
  if (text == null || text.trim() === '') return { ok: true, value: null };
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : 'not JSON' };
  }
}

/** "9m 05s" — the countdown shown for a pending deletion. */
export function countdown(seconds: number | undefined | null): string {
  if (seconds == null) return '';
  const s = Math.max(0, Math.floor(seconds));
  if (s === 0) return 'due now';
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${String(r).padStart(2, '0')}s` : `${r}s`;
}

/** State names the draft adds, removes and changes against its base rows (keyed by name + direction). */
export function localDiff(base: StateRow[], draft: StateRow[]): { added: string[]; removed: string[]; changed: string[] } {
  const key = (r: StateRow) => `${r.name}|${r.call_request ?? ''}`;
  const fields: (keyof StateRow)[] = ['type', 'action', 'context_type', 'action_details', 'type_meta', 'attributes'];
  const norm = (v: unknown) => {
    if (v == null || v === '') return null;
    if (typeof v === 'string') {
      const p = parseJsonField(v);
      return p.ok ? JSON.stringify(sortKeys(p.value)) : v;
    }
    return JSON.stringify(sortKeys(v));
  };
  const b = new Map(base.map(r => [key(r), r]));
  const d = new Map(draft.map(r => [key(r), r]));
  const added: string[] = [];
  const removed: string[] = [];
  const changed: string[] = [];
  d.forEach((r, k) => {
    const o = b.get(k);
    if (!o) { added.push(r.name); return; }
    if (fields.some(f => norm(o[f]) !== norm(r[f]))) changed.push(r.name);
  });
  b.forEach((r, k) => { if (!d.has(k)) removed.push(r.name); });
  return { added: added.sort(), removed: removed.sort(), changed: changed.sort() };
}

function sortKeys(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortKeys);
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    Object.keys(v as Record<string, unknown>).sort().forEach(k => { out[k] = sortKeys((v as Record<string, unknown>)[k]); });
    return out;
  }
  return v;
}

/** Errors that block a publish (pre-existing ones arrive as warnings already). */
export function blockingIssues(issues: Issue[] | undefined): Issue[] {
  return (issues || []).filter(i => i.severity === 'ERROR');
}

/** Parses "SALES, AGENT" / "*" into the exceptions list. */
export function parseExceptions(text: string): string[] {
  return Array.from(new Set(text.split(/[\s,]+/).map(s => s.trim()).filter(Boolean)));
}

export default ivrPublishService;
